





























































































































































































































































import { Component, Prop, Watch } from 'vue-property-decorator';
import GiganautVue from '@/components/Shared/GiganautVue';
import ActivityDetailPane from './ActivityDetailPane.vue';
import ActivitySidePanel from './ActivitySidePanel.vue';
import ActivityDto from '@/models/Activities/ActivityDto';
import ActivityType from '@/models/Activities/ActivityType';
import ActivityStatus, { ActivityStatusLabels } from '@/models/Activities/ActivityStatus';
import ActivityPriority, { ActivityPriorityColors } from '@/models/Activities/ActivityPriority';
import ActivityEntityType from '@/models/Activities/ActivityEntityType';
import OpportunityStage from '@/models/Opportunities/OpportunityStage';
import OpportunityStatus from '@/models/Opportunities/OpportunityStatus';
import ajax from '@/lib/ajax';
import { AppModule } from '@/store/modules/app';
import { AgencySettingsModule } from '@/store/modules/agencySettings';
import AgencyUserStatus from '@/models/AgencyUsers/AgencyUserStatus';
import AgencyUserDto from '@/models/AgencyUsers/AgencyUserDto';
import BranchDto from '@/models/Branches/BranchDto';
import DeskDto from '@/models/Desks/DeskDto';

@Component({
    name: 'TaskGrid',
    components: {
        ActivityDetailPane,
        ActivitySidePanel
    }
})
export default class TaskGrid extends GiganautVue {
    @Prop({ default: null })
    entityType!: ActivityEntityType | null;

    @Prop({ default: null })
    entityId!: string | null;

    @Prop({ default: '' })
    entityEmail!: string;

    // Optional: when the host is an Opportunity, the parent passes the full
    // OpportunityDto so the side panel can resolve recipients from linked
    // records + prospect snapshot in one go.
    @Prop({ default: null })
    entityOpportunity!: any | null;

    ActivityStatus = ActivityStatus;

    // Tasks are frozen once the host opportunity is Won (stage) or Closed
    // (status) — mirrors the backend guard so the UI can't offer a blocked action.
    get activitiesLocked(): boolean {
        if (this.entityType !== ActivityEntityType.Opportunity || !this.entityOpportunity) return false;
        return this.entityOpportunity.stage === OpportunityStage.Won
            || this.entityOpportunity.status === OpportunityStatus.Closed;
    }

    get activitiesLockedReason(): string {
        if (!this.entityOpportunity) return '';
        if (this.entityOpportunity.stage === OpportunityStage.Won) {
            return 'This opportunity has been won, so its tasks are locked. Reopen it to add or edit tasks.';
        }
        if (this.entityOpportunity.status === OpportunityStatus.Closed) {
            return 'This opportunity is closed, so its tasks are locked. Reopen it to add or edit tasks.';
        }
        return '';
    }

    // Data
    loading = false;
    loadingMore = false;
    tasks: ActivityDto[] = [];
    totalCount = 0;
    pageSize = 20;

    // Applied filters
    selectedStatus: number = ActivityStatus.Planned;
    selectedOwner: AgencyUserDto | null = null;
    selectedBranch: BranchDto | null = null;
    selectedDesk: DeskDto | null = null;

    // Filter dialog working state
    filterStatus: number = ActivityStatus.Planned;
    filterOwner: AgencyUserDto | null = null;
    filterBranch: BranchDto | null = null;
    filterDesk: DeskDto | null = null;

    // Filter dialog
    filterDialogVisible = false;

    // Selection
    selectedTaskId: string | null = null;
    selectedTasks: ActivityDto[] = [];

    // Side panel
    sidePanelVisible = false;
    editingActivity: ActivityDto | null = null;
    createMode = false;
    createActivityType: ActivityType | null = null;
    createFromActivity: ActivityDto | null = null;

    // Include related toggle
    includeRelated = true;

    // Delete
    deleteDialogVisible = false;
    deletingActivity: ActivityDto | null = null;
    deleting = false;

    get isEntityMode(): boolean {
        return this.entityType !== null && this.entityId !== null;
    }

    get supportsIncludeRelated(): boolean {
        return this.entityType === ActivityEntityType.Company || this.entityType === ActivityEntityType.Location;
    }

    get headers() {
        const h = [
            { text: 'Subject', value: 'subject' },
            { text: 'Owner', value: 'owner', width: '160px', sortable: false },
            { text: 'Due', value: 'dueAt', width: '100px' }
        ];
        if (!this.isEntityMode) {
            h.push({ text: 'Related To', value: 'relatedTo', width: '180px', sortable: false });
        }
        return h;
    }

    get statusOptions() {
        return [
            { value: ActivityStatus.Planned, label: ActivityStatusLabels[ActivityStatus.Planned] },
            { value: ActivityStatus.Completed, label: ActivityStatusLabels[ActivityStatus.Completed] },
            { value: ActivityStatus.Cancelled, label: ActivityStatusLabels[ActivityStatus.Cancelled] }
        ];
    }

    get activeAgencyUsers(): AgencyUserDto[] {
        return AgencySettingsModule.agencyUsers.filter(
            user => user.status === AgencyUserStatus.Active
        );
    }

    get branches(): BranchDto[] {
        return AgencySettingsModule.branches;
    }

    get filterDesks(): DeskDto[] {
        if (this.filterBranch) {
            return this.filterBranch.desks;
        }
        return [];
    }

    agencyUserDisplayName(item: AgencyUserDto): string {
        return item.person ? `${item.person.firstName} ${item.person.lastName}` : '';
    }

    get hasMore(): boolean {
        return this.tasks.length < this.totalCount;
    }

    get selectedDetailTask(): ActivityDto | null {
        if (!this.selectedTaskId) return null;
        return this.tasks.find(t => t.id === this.selectedTaskId) || null;
    }

    // Client-side status filtering for entity mode (timeline API doesn't support statuses param)
    get filteredTasks(): ActivityDto[] {
        let tasks = this.tasks;
        if (this.isEntityMode) {
            tasks = tasks.filter(t => t.status === this.selectedStatus);
        }
        return tasks;
    }

    async created() {
        if (AgencySettingsModule.agencyUsers.length === 0) {
            await AgencySettingsModule.getAgencyUsers();
        }
        if (AgencySettingsModule.branches.length === 0) {
            await AgencySettingsModule.getBranches();
        }

        // Default status filter to open tasks
        this.selectedStatus = ActivityStatus.Planned;
        this.filterStatus = ActivityStatus.Planned;

        // Emit initial filter state to parent
        this.emitFilterChange();

        await this.loadTasks(true);
    }

    @Watch('selectedTasks')
    onSelectedTasksChange() {
        this.$emit('selection-change', this.selectedTasks);
    }

    @Watch('filterBranch')
    onFilterBranchChange(val: BranchDto | null) {
        if (!val) {
            this.filterDesk = null;
        }
    }

    openFilters() {
        // Sync working state from applied state before opening dialog
        this.filterStatus = this.selectedStatus;
        this.filterOwner = this.selectedOwner;
        this.filterBranch = this.selectedBranch;
        this.filterDesk = this.selectedDesk;
        this.filterDialogVisible = true;
    }

    applyFilters() {
        this.filterDialogVisible = false;
        this.selectedStatus = this.filterStatus;
        this.selectedOwner = this.filterOwner;
        this.selectedBranch = this.filterBranch;
        this.selectedDesk = this.filterDesk;
        this.emitFilterChange();
        this.loadTasks(true);
    }

    emitFilterChange() {
        this.$emit('filter-change', {
            status: this.selectedStatus,
            owner: this.selectedOwner,
            branch: this.selectedBranch,
            desk: this.selectedDesk
        });
    }

    getApiPath(): string {
        if (this.isEntityMode) {
            switch (this.entityType) {
                case ActivityEntityType.Contact:
                    return `/api/v1/agency/contacts/${this.entityId}/activities`;
                case ActivityEntityType.Worker:
                    return `/api/v1/agency/workers/${this.entityId}/activities`;
                case ActivityEntityType.Company:
                    return `/api/v1/agency/companies/${this.entityId}/activities`;
                case ActivityEntityType.Location:
                    return `/api/v1/agency/locations/${this.entityId}/activities`;
                case ActivityEntityType.Job:
                    return `/api/v1/agency/jobs/${this.entityId}/activities`;
                case ActivityEntityType.Booking:
                    return `/api/v1/agency/bookings/${this.entityId}/activities`;
                case ActivityEntityType.Opportunity:
                    return `/api/v1/agency/opportunities/${this.entityId}/activities`;
                default:
                    return '/api/v1/agency/activities';
            }
        }
        return '/api/v1/agency/activities/tasks/my';
    }

    onIncludeRelatedChange() {
        this.loadTasks(true);
    }

    buildFilterParams(): string[] {
        const params: string[] = [];
        if (this.isEntityMode) {
            // Entity mode: use timeline endpoint with type filter
            params.push(`activityTypes=${ActivityType.Task}`);
            if (this.selectedOwner) params.push(`ownerIds=${this.selectedOwner.id}`);
            if (!this.includeRelated) params.push('includeRelated=false');
            // Note: statuses filtered client-side for entity mode
        } else {
            // Top-level mode: use tasks/my endpoint with all filters
            params.push(`statuses=${this.selectedStatus}`);
            if (this.selectedOwner) params.push(`ownerIds=${this.selectedOwner.id}`);
        }
        if (this.selectedBranch) params.push(`branchIds=${this.selectedBranch.id}`);
        if (this.selectedDesk) params.push(`deskIds=${this.selectedDesk.id}`);
        return params;
    }

    async loadTasks(reset = false) {
        this.loading = true;
        if (reset) {
            this.selectedTasks = [];
        }

        try {
            const params: string[] = [];
            params.push(`limit=${this.pageSize}`);
            params.push(`offset=${reset ? 0 : this.tasks.length}`);
            params.push(...this.buildFilterParams());

            const response = await ajax.get(`${this.getApiPath()}?${params.join('&')}`);
            const data = response.data || {};
            const items = data.activities || data.tasks || [];

            if (reset) {
                this.tasks = items;
            } else {
                this.tasks = [...this.tasks, ...items];
            }
            this.totalCount = data.total || 0;
        } catch (error) {
            AppModule.handleApiError(error);
        } finally {
            this.loading = false;
        }
    }

    async loadMore() {
        this.loadingMore = true;
        try {
            const params: string[] = [];
            params.push(`limit=${this.pageSize}`);
            params.push(`offset=${this.tasks.length}`);
            params.push(...this.buildFilterParams());

            const response = await ajax.get(`${this.getApiPath()}?${params.join('&')}`);
            const data = response.data || {};
            const items = data.activities || data.tasks || [];
            this.tasks = [...this.tasks, ...items];
        } catch (error) {
            AppModule.handleApiError(error);
        } finally {
            this.loadingMore = false;
        }
    }

    isOverdue(task: ActivityDto): boolean {
        if (!task.endAt || task.status === ActivityStatus.Completed) return false;
        return new Date(task.endAt) < new Date();
    }

    getPriorityColor(priority: number): string {
        return ActivityPriorityColors[priority] || 'grey';
    }

    getPriorityIcon(priority: number): string {
        switch (priority) {
            case ActivityPriority.Low: return 'mdi-arrow-down';
            //case ActivityPriority.Normal: return 'mdi-minus';
            case ActivityPriority.High: return 'mdi-arrow-up';
            case ActivityPriority.Urgent: return 'mdi-alert';
            default: return '';
        }
    }

    getEntityIcon(entityType: number): string {
        switch (entityType) {
            case ActivityEntityType.Company: return this.IconConstants.Company;
            case ActivityEntityType.Location: return this.IconConstants.Location;
            case ActivityEntityType.Contact: return this.IconConstants.Contact;
            case ActivityEntityType.Worker: return this.IconConstants.Worker;
            case ActivityEntityType.Job: return this.IconConstants.Job;
            case ActivityEntityType.Booking: return this.IconConstants.Booking;
            case ActivityEntityType.Opportunity: return this.IconConstants.Opportunity;
            default: return this.IconConstants.Activity;
        }
    }

    getEntityRoute(item: ActivityDto): string {
        switch (item.primaryEntityType) {
            case ActivityEntityType.Company: return `/agency/companies/${item.companyId}`;
            case ActivityEntityType.Location: return `/agency/locations/${item.locationId}`;
            case ActivityEntityType.Contact: return `/agency/contacts/${item.contactId}`;
            case ActivityEntityType.Worker: return `/agency/workers/${item.workerId}`;
            case ActivityEntityType.Job: return `/agency/jobs/${item.jobId}`;
            case ActivityEntityType.Booking: return `/agency/bookings/${item.bookingId}`;
            case ActivityEntityType.Opportunity: return `/agency/opportunities/${item.opportunityId}`;
            default: return '';
        }
    }

    getOwnerAvatarUrl(owner: { id?: string; avatar?: string }): string {
        if (!owner.avatar || !owner.id) return '';
        const baseUrl = AppModule.currentAgencyContext ? AppModule.currentAgencyContext.storageSharedBaseUrl : '';
        if (!baseUrl) return '';
        return `${baseUrl}/avatars/2/${owner.id}/resized-100/${owner.avatar}`;
    }

    getInitials(person: { firstName?: string; lastName?: string }): string {
        const first = person.firstName ? person.firstName.charAt(0).toUpperCase() : '';
        const last = person.lastName ? person.lastName.charAt(0).toUpperCase() : '';
        return first + last;
    }

    onRowClick(item: ActivityDto) {
        this.selectedTaskId = item.id;
    }

    openCreateTask() {
        if (this.activitiesLocked) return;
        this.editingActivity = null;
        this.createMode = true;
        this.createActivityType = ActivityType.Task;
        this.createFromActivity = null;
        this.sidePanelVisible = true;
    }

    onEdit(activity: ActivityDto) {
        if (this.activitiesLocked) return;
        this.editingActivity = activity;
        this.createMode = false;
        this.createActivityType = null;
        this.sidePanelVisible = true;
    }

    async onComplete(activity: ActivityDto) {
        if (this.activitiesLocked) return;
        try {
            await ajax.put(`/api/v1/agency/activities/${activity.id}/status`, {
                status: ActivityStatus.Completed,
                updatedAt: activity.updatedAt
            });
            await this.loadTasks(true);
        } catch (error) {
            AppModule.handleApiError(error);
        }
    }

    onDelete(activity: ActivityDto) {
        if (this.activitiesLocked) return;
        this.deletingActivity = activity;
        this.deleteDialogVisible = true;
    }

    async confirmDelete() {
        if (!this.deletingActivity) return;
        this.deleting = true;
        try {
            const deletedId = this.deletingActivity.id;
            await ajax.delete(`/api/v1/agency/activities/${deletedId}`);
            this.deleteDialogVisible = false;
            this.deletingActivity = null;
            if (this.selectedTaskId === deletedId) {
                this.selectedTaskId = null;
            }
            this.closeSidePanel();
            await this.loadTasks(true);
        } catch (error) {
            AppModule.handleApiError(error);
        } finally {
            this.deleting = false;
        }
    }

    onCreateFollowUp(activity: ActivityDto) {
        if (this.activitiesLocked) return;
        this.editingActivity = null;
        this.createMode = true;
        this.createActivityType = ActivityType.Task;
        this.createFromActivity = activity;
        this.sidePanelVisible = true;
    }

    closeSidePanel() {
        this.sidePanelVisible = false;
        this.editingActivity = null;
        this.createMode = false;
        this.createActivityType = null;
        this.createFromActivity = null;
    }

    async onActivitySaved() {
        this.closeSidePanel();
        await this.loadTasks(true);
    }

    async onActivityUpdated() {
        await this.loadTasks(true);
    }

    async bulkSetStatus(status: ActivityStatus) {
        if (this.selectedTasks.length === 0) return;
        try {
            const promises = this.selectedTasks.map(task =>
                ajax.put(`/api/v1/agency/activities/${task.id}/status`, {
                    status: status,
                    updatedAt: task.updatedAt
                })
            );
            await Promise.all(promises);
            this.selectedTasks = [];
            await this.loadTasks(true);
        } catch (error) {
            AppModule.handleApiError(error);
        }
    }
}
